import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import CallToAction from "../components/CallToAction";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Define service categories and services
const categories = [
  {
    id: "face",
    name: "Estetski posegi obraza",
    description: "Pomladite in obnovite svoj videz z našimi naprednimi posegi za obraz."
  },
  {
    id: "body",
    name: "Telo in oblikovanje",
    description: "Preoblikujte svoje telo in izboljšajte samozavest z našimi strokovnimi posegi."
  },
  {
    id: "hands",
    name: "Kirurgija rok",
    description: "Specialistični rekonstruktivni posegi za obnovo funkcionalnosti in estetike rok."
  }
];

const services = [
  // Face services
  {
    id: 1,
    title: "Korekcija vek",
    description: "Pomladitev področja okoli oči za bolj svež in spočit videz.",
    icon: "👁️",
    slug: "korekcija-vek",
    category: "face",
    popular: true
  },
  {
    id: 2,
    title: "Pomladitev obraza",
    description: "Celovita pomladitev obraza za naravno zmanjšanje znakov staranja.",
    icon: "✨",
    slug: "pomladitev-obraza",
    category: "face",
    popular: true
  },
  {
    id: 3,
    title: "Korekcija nosu",
    description: "Izboljšanje oblike in funkcije nosu za harmoničen videz.",
    icon: "👃",
    slug: "korekcija-nosu",
    category: "face"
  },
  {
    id: 4,
    title: "Lifting obraza",
    description: "Dolgotrajni rezultati pomladitve z minimalnimi posegi.",
    icon: "🧬",
    slug: "lifting-obraza",
    category: "face"
  },
  // Body services
  {
    id: 5,
    title: "Povečanje prsi",
    description: "Harmonično oblikovanje prsi z naprednimi tehnikami.",
    icon: "👚",
    slug: "povecanje-prsi",
    category: "body",
    popular: true
  },
  {
    id: 6,
    title: "Liposukcija",
    description: "Natančno odstranjevanje maščobe za izboljšano telesno konturo.",
    icon: "🧘",
    slug: "liposukcija",
    category: "body"
  },
  {
    id: 7,
    title: "Oblikovanje trebuha",
    description: "Napredne tehnike za čvrst in oblikovan trebuh.",
    icon: "🏄",
    slug: "oblikovanje-trebuha",
    category: "body",
    popular: true
  },
  {
    id: 8,
    title: "Dvig zadnjice",
    description: "Naravno izboljšanje oblike in volumna zadnjice.",
    icon: "🧖",
    slug: "dvig-zadnjice",
    category: "body"
  },
  // Hand surgery
  {
    id: 9,
    title: "Rekonstrukcija roke",
    description: "Povrnitev funkcionalnosti po poškodbah ali deformacijah.",
    icon: "👋",
    slug: "rekonstrukcija-roke",
    category: "hands",
    popular: true
  },
  {
    id: 10,
    title: "Operacija karpalnega kanala",
    description: "Sprostitev utesnjenih živcev za odpravo bolečin in povrnitev funkcije.",
    icon: "🧤",
    slug: "operacija-karpalnega-kanala",
    category: "hands"
  },
  {
    id: 11,
    title: "Korekcija prstov",
    description: "Poprava deformacij prstov za izboljšano funkcionalnost in videz.",
    icon: "👌",
    slug: "korekcija-prstov",
    category: "hands"
  },
  {
    id: 12,
    title: "Rekonstrukcija po poškodbah",
    description: "Celostna obravnava poškodb za najboljše funkcionalne in estetske rezultate.",
    icon: "🔧",
    slug: "rekonstrukcija-po-poskodbah",
    category: "hands"
  }
];

const ServicesPage = () => {
  const [activeCategory, setActiveCategory] = useState("all");

  const filteredServices = activeCategory === "all" 
    ? services 
    : services.filter(service => service.category === activeCategory);

  return (
    <Layout>
      <PageHeader
        title="Naše storitve"
        description="Pregled vseh estetskih in rekonstruktivnih storitev, ki jih ponuja naša klinika."
      />
      
      {/* Categories Filter */}
      <section className="bg-white py-12">
        <div className="container mx-auto px-6 md:px-8">
          <div className="flex flex-wrap gap-3 justify-center mb-8">
            <Button 
              variant={activeCategory === "all" ? "default" : "outline"} 
              className={`rounded-full ${activeCategory === "all" ? 'bg-medical-500' : 'border-medical-300'}`}
              onClick={() => setActiveCategory("all")}
            >
              Vse storitve
            </Button>
            
            {categories.map(category => (
              <Button 
                key={category.id}
                variant={activeCategory === category.id ? "default" : "outline"}
                className={`rounded-full ${activeCategory === category.id ? 'bg-medical-500' : 'border-medical-300'}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>
      
      {/* Services Grid */}
      <section className="bg-gray-50 py-12 md:py-16">
        <div className="container mx-auto px-6 md:px-8">
          {activeCategory !== "all" && (
            <div className="max-w-3xl mx-auto text-center mb-12">
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">
                {categories.find(cat => cat.id === activeCategory)?.name}
              </h2>
              <p className="text-gray-700">
                {categories.find(cat => cat.id === activeCategory)?.description}
              </p>
            </div>
          )}
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {filteredServices.map((service) => (
              <Link 
                key={service.id} 
                to={`/storitev/${service.slug}`}
                className="block"
              >
                <div className={`glass-card h-full p-6 md:p-8 transition-all duration-300 hover:translate-y-[-5px] relative ${
                  service.popular ? 'border-medical-300' : ''
                }`}>
                  {service.popular && (
                    <div className="absolute -top-3 -right-3 bg-medical-500 text-white text-xs px-3 py-1 rounded-full">
                      Priljubljeno
                    </div>
                  )}
                  <div className="text-3xl mb-4">{service.icon}</div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-gray-600 mb-6">
                    {service.description}
                  </p>
                  <div className="flex items-center text-medical-600 hover:text-medical-700 font-medium">
                    Več o storitvi
                    <ArrowRight size={16} className="ml-2" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
      
      <CallToAction />
    </Layout>
  );
};

export default ServicesPage;